"use client";

import dynamic from "next/dynamic";
import { docsHref } from "@/lib/docs-links";
import { useEffect, useState } from "react";
import { CONSENT_COOKIE, CONSENT_PENDING_COOKIE, CONSENT_VERSION, CONSENT_MAX_AGE, cookieValue, parseConsent, consentCookie, type Consent, type ConsentChoice } from "@/lib/analytics/consent";
import { activateConsent, clearOptionalStorage } from "@/lib/analytics/consent-client";

const OptionalTracking = dynamic(() => import("./optional-tracking").then(m => m.OptionalTracking), { ssr: false });
const CHANGE_KEY = "mlx:consent-change";
const PENDING_KEY = "mlx:consent-pending";

export function CookieConsent() {
  const [consent, setConsent] = useState<Consent | null>(null);
  const [ready, setReady] = useState(false);
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(false);
  const [choice, setChoice] = useState<ConsentChoice>({ analytics: false, advertising: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const stored = parseConsent(cookieValue(document.cookie, CONSENT_COOKIE));
    const finish = (c: Consent | null) => {
      if (cancelled) return;
      activateConsent(c); setConsent(c); setChoice(c ?? { analytics: false, advertising: false });
      setReady(true); setOpen(!c);
      if (!c || (!c.analytics && !c.advertising)) clearOptionalStorage();
    };
    let pending: string | null = cookieValue(document.cookie, CONSENT_PENDING_COOKIE) ? JSON.stringify({ analytics: false, advertising: false }) : null;
    try { pending ||= localStorage.getItem(PENDING_KEY); } catch { /* necessary cookie supports storage-restricted browsers */ }
    if (pending) {
      void fetch("/api/marketing/consent", { method: "POST", headers: { "Content-Type": "application/json" }, body: pending })
        .then(async r => {
          if (!r.ok) throw new Error("Your server-side choice has not been saved. Please retry Cookie settings. Optional tracking is off in this browser.");
          try { localStorage.removeItem(PENDING_KEY); } catch { /* unavailable */ }
          document.cookie = `${CONSENT_PENDING_COOKIE}=; Path=/; Max-Age=0; Secure; SameSite=Lax`;
          finish((await r.json()).consent);
        }).catch(e => { finish(null); setError(e.message); });
    } else if (!stored) finish(null);
    else if (!stored.analytics && !stored.advertising) finish(stored);
    else void fetch("/api/marketing/consent", { cache: "no-store" })
      .then(async r => r.ok ? (await r.json()).consent : null).then(finish).catch(() => finish(null));
    const show = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target.closest('a[href="#cookie-settings"]') : null;
      if (!target) return;
      event.preventDefault(); setSettings(true); setOpen(true);
    };
    const sync = (event: StorageEvent) => { if (event.key === CHANGE_KEY) { activateConsent(null); location.reload(); } };
    document.addEventListener("click", show);
    window.addEventListener("storage", sync);
    const check = () => {
      const current = parseConsent(cookieValue(document.cookie, CONSENT_COOKIE));
      if (stored && (!current || current.revision !== stored.revision)) { activateConsent(null); location.reload(); }
    };
    const expiry = window.setInterval(check, 1000);
    window.addEventListener("focus", check);
    return () => { cancelled = true; clearInterval(expiry); document.removeEventListener("click", show); window.removeEventListener("storage", sync); window.removeEventListener("focus", check); };
  }, []);

  async function save(next: ConsentChoice) {
    setBusy(true); setError(""); activateConsent(null); setConsent(null);
    try {
      const r = await fetch("/api/marketing/consent", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(next) });
      if (!r.ok) throw new Error("Could not save your choice.");
      try { localStorage.removeItem(PENDING_KEY); } catch { /* unavailable */ }
      document.cookie = `${CONSENT_PENDING_COOKIE}=; Path=/; Max-Age=0; Secure; SameSite=Lax`;
      clearOptionalStorage();
      try { localStorage.setItem(CHANGE_KEY, crypto.randomUUID()); } catch { /* same-tab reload still stops SDK timers */ }
      // Vendor scripts may retain timers/listeners; removing a script isn't a teardown.
      location.reload();
    } catch {
      // Fail closed locally, retain only a necessary retry instruction, and
      // unload SDKs even when the server cannot yet acknowledge withdrawal.
      document.cookie = consentCookie({ analytics: false, advertising: false, version: CONSENT_VERSION, revision: crypto.randomUUID(), expiresAt: Date.now() + CONSENT_MAX_AGE * 1000 });
      document.cookie = `${CONSENT_PENDING_COOKIE}=1; Path=/; Max-Age=${CONSENT_MAX_AGE}; Secure; SameSite=Lax`;
      clearOptionalStorage();
      try { localStorage.setItem(PENDING_KEY, JSON.stringify({ analytics: false, advertising: false })); localStorage.setItem(CHANGE_KEY, crypto.randomUUID()); } catch { /* preference cookie still denies */ }
      location.reload();
    }
  }

  return <>
    {ready && consent && (consent.analytics || consent.advertising) ? <OptionalTracking consent={consent} /> : null}
    {ready && open ? <section role="region" aria-label="Cookie preferences" className="fixed inset-x-0 bottom-0 z-[100] max-h-[calc(100dvh-4rem)] overflow-y-auto border-t border-neutral-300 bg-white px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] text-neutral-950 shadow-xl sm:inset-x-3 sm:bottom-3 sm:mx-auto sm:max-h-[calc(100dvh-1.5rem)] sm:max-w-2xl sm:rounded-xl sm:border sm:p-6">
      <h2 className="text-base font-semibold sm:text-lg">Your privacy choices</h2>
      <p className="mt-1.5 text-[13px] leading-5 sm:mt-2 sm:text-sm sm:leading-6">We use necessary storage to remember your choices and keep the site working. With your permission, we also measure visits and advertising results. Rejecting optional tracking won’t affect your enquiry.</p>
      <p className="mt-1.5 flex flex-wrap items-center gap-x-5 text-[13px] sm:mt-2 sm:text-sm"><a className="underline" href={docsHref("/policies/privacy")}>Privacy Policy</a>{settings ? null : <button type="button" onClick={() => setSettings(true)} className="min-h-11 underline sm:min-h-0">Settings</button>}</p>
      {settings ? <fieldset className="my-4 space-y-3" disabled={busy}>
        <legend className="sr-only">Optional tracking</legend>
        <label className="flex items-start gap-3 text-sm"><input type="checkbox" checked={choice.analytics} onChange={e => setChoice({ ...choice, analytics: e.target.checked })} className="mt-1" /><span><strong>Analytics</strong><br />Visit counts and site performance, including PostHog, Vercel and browser diagnostics.</span></label>
        <label className="flex items-start gap-3 text-sm"><input type="checkbox" checked={choice.advertising} onChange={e => setChoice({ ...choice, advertising: e.target.checked })} className="mt-1" /><span><strong>Advertising</strong><br />MLX attribution, Dub and OpenAI measurement, including enquiry conversions and hashed-email matching.</span></label>
      </fieldset> : null}
      {error ? <p role="alert" className="mt-3 text-sm text-red-800">{error}</p> : null}
      <div className="mt-3 grid grid-cols-2 gap-2 sm:mt-4 sm:flex sm:flex-wrap">
        <button disabled={busy} onClick={() => void save({ analytics: false, advertising: false })} className="min-h-11 flex-1 rounded-md border border-neutral-800 px-4 py-2 text-sm font-medium disabled:opacity-50">Reject optional</button>
        <button disabled={busy} onClick={() => void save({ analytics: true, advertising: true })} className="min-h-11 flex-1 rounded-md border border-neutral-800 px-4 py-2 text-sm font-medium disabled:opacity-50">Accept optional</button>
        {settings ? <button disabled={busy} onClick={() => void save(choice)} className="col-span-2 min-h-11 rounded-md border px-4 py-2 text-sm disabled:opacity-50">Save settings</button> : null}
        {consent ? <button onClick={() => setOpen(false)} className="col-span-2 min-h-11 px-3 text-sm underline sm:col-span-1">Close</button> : null}
      </div>
    </section> : null}
    <noscript><p className="p-3 text-sm">Optional tracking is disabled without JavaScript.</p></noscript>
  </>;
}
